import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getApiErrorMessage } from "../config/api";
import { productService } from "../services/productService";
import { normalizeNicheCategoryState } from "../utils/productManagement";
import { ArrowRight, Grid3X3, Loader2, RotateCcw, Save } from "lucide-react";

const arraysEqual = (left, right) => {
  if (left.length !== right.length) return false;

  const leftSorted = [...left].sort();
  const rightSorted = [...right].sort();
  return leftSorted.every((value, index) => value === rightSorted[index]);
};

const getNicheSaveErrorMessage = (error) => {
  const message = getApiErrorMessage(error, "Could not update niche categories.");
  const normalizedMessage = message.toLowerCase();

  if (
    normalizedMessage.includes("active product") ||
    (normalizedMessage.includes("remove") && normalizedMessage.includes("product"))
  ) {
    return "You cannot remove a niche category that still has active products. Reassign those products to another approved category or archive them first.";
  }

  return message;
};

export default function Categories() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = user?.token;

  const [draftSelectedIds, setDraftSelectedIds] = useState<string[]>([]);
  const [saveError, setSaveError] = useState("");

  const nicheQuery = useQuery({
    queryKey: ["my-niche-categories"],
    queryFn: async () =>
      normalizeNicheCategoryState(await productService.getMyNicheCategories()),
    enabled: Boolean(token),
  });

  const saveMutation = useMutation({
    mutationFn: async (nicheCategoryIds: string[]) =>
      productService.updateMyNicheCategories({ nicheCategoryIds }),
  });

  const nicheState = nicheQuery.data || normalizeNicheCategoryState({});
  const loading = Boolean(token) && nicheQuery.isLoading;
  const error = !token
    ? ""
    : nicheQuery.error
      ? getApiErrorMessage(nicheQuery.error, "Could not load niche categories.")
      : "";

  const serverSelectedIds = useMemo(
    () => nicheState.selectedCategories.map((category) => category.id),
    [nicheState.selectedCategories],
  );

  const serverSelectedKey = useMemo(
    () => [...serverSelectedIds].sort().join("|"),
    [serverSelectedIds],
  );

  useEffect(() => {
    setDraftSelectedIds(serverSelectedKey ? serverSelectedKey.split("|") : []);
  }, [serverSelectedKey]);

  const availableCategories = useMemo(
    () =>
      nicheState.availableCategories
        .filter((category) => category.id)
        .slice()
        .sort((left, right) => left.name.localeCompare(right.name)),
    [nicheState.availableCategories],
  );

  const selectedCategoryLookup = useMemo(() => {
    const categoryMap = new Map();

    nicheState.availableCategories.forEach((category) => {
      if (category.id) {
        categoryMap.set(category.id, category);
      }
    });

    nicheState.selectedCategories.forEach((category) => {
      if (category.id) {
        categoryMap.set(category.id, category);
      }
    });

    return categoryMap;
  }, [nicheState.availableCategories, nicheState.selectedCategories]);

  const selectedCategoryChips = useMemo(
    () =>
      draftSelectedIds.map((categoryId) => {
        const category = selectedCategoryLookup.get(categoryId);
        return category || { id: categoryId, name: categoryId, description: "" };
      }),
    [draftSelectedIds, selectedCategoryLookup],
  );

  const selectedIdSet = useMemo(
    () => new Set(draftSelectedIds),
    [draftSelectedIds],
  );

  const hasChanges = !arraysEqual(draftSelectedIds, serverSelectedIds);
  const maxCategories = nicheState.maxCategories;
  const canMutate = !nicheState.isBanned;

  const toggleCategory = (categoryId) => {
    if (!canMutate) {
      return;
    }

    setSaveError("");

    setDraftSelectedIds((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      }

      if (maxCategories > 0 && prev.length >= maxCategories) {
        setSaveError(`Your plan allows a maximum of ${maxCategories} categories.`);
        return prev;
      }

      return [...prev, categoryId];
    });
  };

  const handleReset = () => {
    setDraftSelectedIds(serverSelectedIds);
    setSaveError("");
  };

  const handleSave = async () => {
    if (!token || !canMutate || !hasChanges || saveMutation.isPending) {
      return;
    }

    if (maxCategories > 0 && draftSelectedIds.length > maxCategories) {
      setSaveError(`Your plan allows a maximum of ${maxCategories} categories.`);
      return;
    }

    setSaveError("");

    try {
      await saveMutation.mutateAsync(draftSelectedIds);
      await queryClient.invalidateQueries({ queryKey: ["my-niche-categories"] });
    } catch (nextError) {
      setSaveError(getNicheSaveErrorMessage(nextError));
    }
  };

  const summaryCards = [
    {
      label: "Plan Category Limit",
      value: String(maxCategories || 0),
      hint: "Maximum approved niche categories",
    },
    {
      label: "Selected",
      value: String(draftSelectedIds.length),
      hint: "Categories in your current draft",
    },
    {
      label: "Remaining",
      value: String(
        maxCategories > 0
          ? Math.max(maxCategories - draftSelectedIds.length, 0)
          : 0,
      ),
      hint: "Selections left under your plan",
    },
    {
      label: "Product Cap",
      value: String(nicheState.maxProducts || 0),
      hint: nicheState.shopName || user?.shopName || user?.name || "Seller",
    },
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Niche Categories
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
            This page loads your seller niche-category state first, lets you
            update the approved selection within plan limits, and re-syncs from
            the API after every successful save.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/dashboard/products")}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800">
          Product Listings <ArrowRight size={15} />
        </button>
      </div>

      {nicheState.isBanned && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
          This seller is currently restricted. Niche category changes are
          disabled until the restriction is removed.
        </div>
      )}

      {(error || saveError) && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
          {error || saveError}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => (
          <div
            key={card.label}
            className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
              {card.label}
            </p>
            <p className="mt-3 text-2xl font-bold text-gray-900 dark:text-gray-100">
              {card.value}
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {card.hint}
            </p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-900">
          <Loader2 size={34} className="mx-auto animate-spin text-violet-500" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Loading your niche category permissions...
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500 dark:text-gray-400">
                  Selected Categories
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {draftSelectedIds.length > 0 ? (
                    selectedCategoryChips.map((category) => (
                        <span
                          key={category.id}
                          className="rounded-full bg-violet-50 px-3 py-1.5 text-sm font-medium text-violet-700 dark:bg-violet-500/10 dark:text-violet-200">
                          {category.name}
                        </span>
                      ))
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      No categories selected yet.
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={!hasChanges || saveMutation.isPending}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
                  <RotateCcw size={15} /> Reset
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!hasChanges || saveMutation.isPending || !canMutate}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60">
                  {saveMutation.isPending ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <Save size={15} />
                  )}
                  Save Categories
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {availableCategories.map((category) => {
              const selected = selectedIdSet.has(category.id);
              const disabled =
                !selected &&
                maxCategories > 0 &&
                draftSelectedIds.length >= maxCategories;

              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => toggleCategory(category.id)}
                  disabled={disabled || !canMutate}
                  className={`rounded-2xl border p-5 text-left transition ${
                    selected
                      ? "border-violet-500 bg-violet-50 dark:border-violet-400 dark:bg-violet-500/10"
                      : "border-gray-200 bg-white hover:border-violet-300 hover:bg-violet-50/40 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-violet-500/40 dark:hover:bg-violet-500/5"
                  } ${
                    disabled || !canMutate
                      ? "cursor-not-allowed opacity-60"
                      : "cursor-pointer"
                  }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-500/10 text-violet-500 dark:bg-violet-500/15 dark:text-violet-300">
                        <Grid3X3 size={18} />
                      </div>
                      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {category.name}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
                        {category.description ||
                          "Approved sellers can attach products to this category."}
                      </p>
                    </div>

                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        selected
                          ? "bg-violet-600 text-white"
                          : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                      }`}>
                      {selected ? "Selected" : "Available"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {availableCategories.length === 0 && (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-14 text-center dark:border-gray-700 dark:bg-gray-900">
              <Grid3X3
                size={42}
                className="mx-auto mb-4 text-gray-300 dark:text-gray-700"
              />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                No niche categories returned
              </h3>
              <p className="mx-auto mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
                The seller niche-category endpoint did not return any
                available categories for this account.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
